import { useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useAuthStore } from '@/stores/authStore';
import { MobileAccountPanel } from '@/components/AccountMenu';
import { MARKETPLACE } from '@/lib/marketplacePaths';
import {
  storefrontNavIsActive,
  storefrontNavStripLinks,
  type StorefrontNavFlags,
} from '@/lib/storefrontNavigation';
import { useFeatureFlag } from '@/hooks/usePlatformConfig';

function HomeIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.126 1.126 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  );
}

function ShopIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 2.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />
    </svg>
  );
}

function ExploreIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <circle cx="12" cy="12" r="8" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.9 9.1l-1.8 4.2-4.2 1.8 1.8-4.2 4.2-1.8z" />
    </svg>
  );
}

function PropertyIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 9.75v10.5m15-10.5v10.5M8.25 9.75V5.25a.75.75 0 01.75-.75h6a.75.75 0 01.75.75v4.5M12 21v-3.375c0-.621.504-1.125 1.125-1.125h.375c.621 0 1.125.504 1.125 1.125V21M12 9.75V5.25" />
    </svg>
  );
}

function ClassifiedsIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.008v.008H3.75V6.75zm0 5.25h.008v.008H3.75V12zm0 5.25h.008v.008H3.75V17.25z" />
    </svg>
  );
}

function BusinessIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 9.75v10.5m15-10.5v10.5M8.25 9.75V6.375c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125V9.75M9 21v-4.875c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
    </svg>
  );
}

function ProIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
    </svg>
  );
}

function DealsIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  );
}

const EXPLORE_SHEET_ICONS: Record<string, React.ReactNode> = {
  property: <PropertyIcon />,
  classifieds: <ClassifiedsIcon />,
  vendors: <BusinessIcon />,
  'find-a-pro': <ProIcon />,
  deals: <DealsIcon />,
};

function exploreNavIsActive(pathname: string, flags: StorefrontNavFlags): boolean {
  return storefrontNavStripLinks(flags)
    .filter((link) => link.id !== 'shop')
    .some((link) => storefrontNavIsActive(pathname, link));
}

function NavTab({
  to,
  label,
  icon,
  exact = false,
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
  exact?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        clsx(
          'relative flex min-h-11 min-w-11 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-[10px] font-medium transition-colors',
          isActive ? 'text-brand-700 dark:text-brand-400' : 'text-muted hover:text-default',
        )
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

/** Mobile nav — Home · Shop · Explore · Sign in (cart stays in header). */
export function MobileBottomNav() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const [accountOpen, setAccountOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const propertyListingsEnabled = useFeatureFlag('propertyListings');
  const classifiedsEnabled = useFeatureFlag('classifieds');
  const businessDirectoryEnabled = useFeatureFlag('businessDirectory');

  const navFlags = useMemo(
    () => ({
      propertyListings: propertyListingsEnabled,
      classifieds: classifiedsEnabled,
      businessDirectory: businessDirectoryEnabled,
    }),
    [propertyListingsEnabled, classifiedsEnabled, businessDirectoryEnabled],
  );

  const exploreLinks = useMemo(
    () => storefrontNavStripLinks(navFlags).filter((link) => link.id !== 'shop'),
    [navFlags],
  );

  const exploreActive = exploreNavIsActive(location.pathname, navFlags);

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 flex border-t border-default bg-surface-raised md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        aria-label="Main navigation"
      >
        <NavTab to="/" label="Home" icon={<HomeIcon />} exact />
        <NavTab to={MARKETPLACE.shop} label="Shop" icon={<ShopIcon />} />
        <button
          type="button"
          onClick={() => setExploreOpen(true)}
          aria-expanded={exploreOpen}
          aria-haspopup="dialog"
          className={clsx(
            'relative flex min-h-11 min-w-11 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-[10px] font-medium transition-colors',
            exploreActive ? 'text-brand-700 dark:text-brand-400' : 'text-muted hover:text-default',
          )}
        >
          <ExploreIcon />
          <span>Explore</span>
        </button>
        <button
          type="button"
          onClick={() => setAccountOpen(true)}
          className="relative flex min-h-11 min-w-11 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-[10px] font-medium text-muted transition-colors hover:text-default"
        >
          <UserIcon />
          <span>{user ? 'Account' : 'Sign in'}</span>
        </button>
      </nav>
      {exploreOpen ? (
        <div className="fixed inset-0 z-[60] md:hidden" role="dialog" aria-modal="true" aria-label="Explore ShopGND">
          <button
            type="button"
            aria-label="Close explore menu"
            className="absolute inset-0 bg-black/40"
            onClick={() => setExploreOpen(false)}
          />
          <div
            className="absolute inset-x-0 bottom-0 rounded-t-2xl border border-default bg-surface-raised p-4 shadow-xl"
            style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
          >
            <p className="mb-3 text-sm font-semibold text-default">Explore ShopGND</p>
            <div className="grid grid-cols-3 gap-2">
              {exploreLinks.map((link) => (
                <Link
                  key={link.id}
                  to={link.to}
                  onClick={() => setExploreOpen(false)}
                  className="flex min-h-11 flex-col items-center justify-center gap-1 rounded-xl border border-default px-3 py-3 text-xs font-medium text-default hover:bg-surface-sunken"
                >
                  {EXPLORE_SHEET_ICONS[link.id]}
                  <span>{link.id === 'find-a-pro' ? 'Find a pro' : link.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      ) : null}
      <MobileAccountPanel open={accountOpen} onClose={() => setAccountOpen(false)} />
    </>
  );
}
